import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Button, Result, Spin } from 'antd';
import { useAuth } from './OlAuthContext';

/** Full-viewport centring for auth transition states. */
function CenterState({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Gates the app behind authentication. Reject-closed: an unauthenticated session is redirected to
 * the IdP, never shown protected content. In mock mode the user is always authenticated.
 */
export function ProtectedRoute() {
  const auth = useAuth();

  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      auth.login();
    }
  }, [auth]);

  if (auth.status === 'authenticated') {
    return <Outlet />;
  }

  if (auth.status === 'error') {
    return (
      <CenterState>
        <Result
          status="error"
          title="Sign-in failed"
          subTitle={auth.error ?? 'The identity provider rejected the session.'}
          extra={
            <Button type="primary" onClick={auth.login}>
              Try again
            </Button>
          }
        />
      </CenterState>
    );
  }

  return (
    <CenterState>
      <Spin size="large" />
      <div style={{ color: 'var(--ol-text-tertiary)' }}>
        {auth.status === 'unauthenticated' ? 'Redirecting to sign-in…' : 'Restoring session…'}
      </div>
    </CenterState>
  );
}
